import { CameraScanner } from '../components/CameraScanner'
import { t } from '../i18n'

/** Left pane of the scan screen (Figma 2464:2501): the tap target, and then the live camera in
 * exactly the same box.
 *
 * The camera does NOT open on its own. iOS/WKWebView will not start audio without a user gesture
 * *inside the web view*, and opening the extension is a tap on native POS chrome, which does not
 * count. With no gesture the scan cue — the cashier's only confirmation that a scan landed —
 * fires silently from the QR-decode callback. So the pane is one large tap target first; that tap
 * resumes the AudioContext (`installAudioUnlock`, on `pointerdown`) and then starts
 * `getUserMedia`, which is also Lightspeed's recommended way to open the camera.
 *
 * Scanning replaces the pane's contents IN PLACE rather than swapping the whole screen, so the
 * search panel beside it never disappears and the feed opens where the cashier was already
 * looking.
 */
export function ScanLobby({
  scanning,
  facingMode,
  onStartScan,
  onFlipCamera,
  onDecode,
}: {
  scanning: boolean
  facingMode: 'user' | 'environment'
  onStartScan: () => void
  onFlipCamera: () => void
  onDecode: (cardId: string) => void
}) {
  return (
    <div className="relative min-w-0 flex-1 overflow-hidden bg-[#12121a]">
      {scanning ? (
        <>
          <CameraScanner onDecode={onDecode} facingMode={facingMode} />
          {/* Reticle + captions sit over the feed. `pointer-events-none` so none of it can swallow
            * a tap meant for the video underneath. */}
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-6">
            <span className="size-[292px] max-w-full shrink-0 rounded-[2px] border-2 border-white" />
            <span className="mt-[68px] text-center text-[24px] font-bold text-white">
              {t('scanCustomerQr')}
            </span>
            <span className="mt-2 text-center text-[15px] text-white/75">
              {t('scanCameraHint')}
            </span>
          </div>
        </>
      ) : (
        <button
          type="button"
          onClick={onStartScan}
          aria-label={t('tapToStartScan')}
          className="absolute inset-0 flex items-center justify-center focus:outline-none"
        >
          <span className="px-8 text-center text-[24px] text-white">{t('tapToStartScan')}</span>
        </button>
      )}

      {/* Below --ls-safe-top: the Lightspeed host header overlays our top ~60px. There is no
       * close button here on purpose — the POS draws its own ✕ on that header, and a second one
       * inside the web view is just a way to close the wrong thing. */}
      <img
        src="/brand/badger-wordmark-white.svg"
        alt="Badger"
        className="pointer-events-none absolute left-[50px] top-[calc(var(--ls-safe-top)+16px)] h-[29px] w-[105px]"
      />

      {!scanning ? (
        <img
          src="/brand/tap-hand.svg"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[38px] left-1/2 h-[80px] w-[55px] -translate-x-1/2"
        />
      ) : (
        // Rear camera is the default (DEV-308) because the cashier points the iPad at the
        // customer's phone; the flip is kept for a register mounted facing the customer.
        <button
          type="button"
          onClick={onFlipCamera}
          className="absolute bottom-[32px] left-[50px] rounded-lg border border-white/25 px-5 py-2.5 text-[16px] font-medium text-white/85 transition hover:bg-white/10"
        >
          {t('changeCamera')}
        </button>
      )}
    </div>
  )
}
